import { IContext, IConversationBlock, ISuggestion, IUserMessage } from '@/models/bot-models';
import fluidAutoScrollStore from '@/store/fluid-autoscroll';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import Vue from 'vue';
import Vuex, { ActionContext, ActionTree, MutationTree } from 'vuex';

Vue.use(Vuex);

export interface RootState {
    conversationBlocks: IConversationBlock[];
    currentUserMessage: IUserMessage | null;
    isTyping: boolean;
    suggestions: ISuggestion[];
    context: IContext | null;
    chatErrorMessage: string | null;
    iframeSrc: string | null;
    hasNewMessages$: Subject<boolean>;
    greetingMessage: boolean;
    startButtonHidden: boolean;
}

const rootState: RootState = {
    conversationBlocks: [],
    currentUserMessage: null,
    isTyping: false,
    suggestions: [],
    context: null,
    chatErrorMessage: null,
    iframeSrc: null,
    hasNewMessages$: new Subject(),
    greetingMessage: false,
    startButtonHidden: false,
};

const storeMutations: MutationTree<RootState> = {
    addConversationBlock(state: RootState, cb: IConversationBlock) {
        state.conversationBlocks.push(cb);
    },
    setCurrentUserMessage(state: RootState, u: IUserMessage) {
        state.currentUserMessage = u;
    },
    setIsTyping(state: RootState, isTyping: boolean) {
        state.isTyping = isTyping;
    },
    setSuggestions(state: RootState, suggestions: ISuggestion[]) {
        state.suggestions = suggestions;
    },
    setChatErrorMessage(state: RootState, messageContent: string | null) {
        state.chatErrorMessage = messageContent;
    },
    setConversationContext(state: RootState, c: IContext) {
        state.context = c;
    },
    setIframeSrc(state: RootState, src: string) {
        state.iframeSrc = src;
    },
    setHasNewMessages(state: RootState) {
        state.hasNewMessages$.next(true);
    },
    setGreetingMessage(state: RootState, greetingMessage: boolean) {
        state.greetingMessage = greetingMessage;
    },
    setStartButtonHidden(state: RootState, startButtonShouldBeHidden: boolean) {
        state.startButtonHidden = startButtonShouldBeHidden;
    },
};

const storeActions: ActionTree<RootState, RootState> = {
    renderConversationBlock(context: ActionContext<RootState, RootState>, conversationBlock: IConversationBlock) {
        context.commit('setIsTyping', false);
        for (const botAnswer of conversationBlock.bot_message.answers) {
            if ('suggestions' in botAnswer.content) {
                context.commit('setSuggestions', botAnswer.content.suggestions);
            }
        }
        context.commit('addConversationBlock', conversationBlock);

        if (
            document.hidden &&
            Notification.permission === 'granted' &&
            conversationBlock.bot_message.answers.length !== 0
        ) {
            const textContent: any | undefined = conversationBlock.bot_message.answers.find(
                (answer) => answer.content_type === 'text',
            )?.content;
            const options = {
                body: textContent?.value ?? 'Nouveaux messages',
                icon: 'assets/img/randy-icon.png',
            };
            const notification = new Notification('Tu as reçu de nouveaux messages', options);
            notification.onclick = function() {
                this.close();
                window.focus();
            };
        }
    },

    popChatIfNotDisplayed(context: ActionContext<RootState, RootState>, conversationBlock: IConversationBlock) {
        if (conversationBlock.bot_message.answers.length > 0) {
            context.commit('setHasNewMessages');
        }
    },

    showPendingUserMessage(context: ActionContext<RootState, RootState>, message: IUserMessage) {
        context.commit('setSuggestions', []);
        context.commit('setCurrentUserMessage', message);
        context.commit('setIsTyping', true);
        setTimeout(() => {
            if (context.state.isTyping && _.isEqual(context.state.currentUserMessage, message)) {
                context.commit('setChatErrorMessage', 'Une erreur est survenue... Merci de rafraichir la page !');
            }
        }, 30000);
    },

    showChatErrorMessage(
        context: ActionContext<RootState, RootState>,
        messageData: { content: string; millisDelay?: number },
    ) {
        context.commit('setChatErrorMessage', messageData.content);
        setTimeout(
            () => {
                context.commit('setChatErrorMessage', null);
            },
            typeof messageData.millisDelay !== 'undefined' ? messageData.millisDelay : 3000,
        );
    },

    showGreetingMessage(context: ActionContext<RootState, RootState>) {
        context.commit('setGreetingMessage', true);
    },

    hideGreetingMessage(context: ActionContext<RootState, RootState>) {
        context.commit('setGreetingMessage', false);
    },

    showStartButton(context: ActionContext<RootState, RootState>) {
        context.commit('setStartButtonHidden', false);
    },

    hideStartButton(context: ActionContext<RootState, RootState>) {
        context.commit('setStartButtonHidden', true);
    },
};

export function createStore() {
    return new Vuex.Store({
        state: rootState,
        mutations: storeMutations,
        actions: storeActions,
        modules: {
            scroll: fluidAutoScrollStore,
        },
    });
}

export default createStore();
